import { utilsObject } from "@app/common";
import { createContext, useCallback, useEffect, useState } from "react";

import { AppElementsProviderEventTypes } from "../context/AppElementsProviderEventTypes";
import { appElementsProvider } from "../services/appElements";
import { applyThemeToHtmlElement } from "../utils/themeUtils";

type TThemeKey = "dark" | "light" | "system";

type TSize = "xxl" | "xl" | "lg" | "base" | "sm" | "xs";

export interface IOverrides {
    title: {
        [key in TSize]: IOverride;
    };
}

export interface IOverride {
    className?: string;
    style?: React.CSSProperties;
}

export interface ITheme {
    key: TThemeKey;
    overrides?: IOverrides;
    colors?: IThemeColors;
    borderRadius?: IThemeBorderRadius;
}

export interface IThemeColors {
    primary: string;
}

export interface IThemeBorderRadius {
    none: string;
    sm: string;
    default: string;
    md: string;
    lg: string;
    xl: string;
    "2xl": string;
    "3xl": string;
    full: string;
}

export interface IThemeProviderProps {
    children: React.ReactNode;
    defaultTheme?: TThemeKey;
    storageKey?: string;
    storageOverridesKey?: string;
}

interface ThemeProviderState {
    theme: ITheme;
    setTheme: (theme: ITheme) => boolean;
}

const initialState: ThemeProviderState = {
    theme: { key: "system" },
    setTheme: () => false,
};

export const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "hc-ibe-theme",
    storageOverridesKey = "hc-ibe-theme-overrides",
    ...props
}: IThemeProviderProps) {
    const appElements = appElementsProvider.getAppElements();

    const [theme, setTheme] = useState<ITheme>(() => {
        try {
            return JSON.parse(localStorage.getItem(storageKey) as string) || { key: defaultTheme };
        } catch {
            return { key: defaultTheme };
        }
    });

    useEffect(() => {
        const container = appElements.container!;
        const root = container.querySelector<HTMLDivElement>("hc-ibe")?.shadowRoot?.firstChild as HTMLDivElement;

        if (root) {
            container.classList.remove("light", "dark");

            root.classList.remove("light", "dark");

            if (theme.key === "system") {
                const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

                root.classList.add(systemTheme);

                return;
            }

            root.classList.add(theme.key);
        }
    }, [theme.key]);

    useEffect(
        () =>
            appElementsProvider.on(AppElementsProviderEventTypes.StyledContainerRegistered, (styledContainerElement: HTMLElement) => {
                applyTheme(theme, styledContainerElement);
            }),
        [theme]
    );

    useEffect(() => {
        appElementsProvider.getRegisteredStyledContainers().forEach((styledContainerElement: HTMLElement) => {
            applyTheme(theme, styledContainerElement);
        });
    }, [theme]);

    const setNextTheme = (nextTheme: ITheme): boolean => {
        const targetElement = appElementsProvider.getIbeElement();

        const forcedThemeOverrides = JSON.parse(localStorage.getItem(storageOverridesKey) || "{}") as ITheme;

        localStorage.setItem(storageKey, JSON.stringify(nextTheme));

        const mergedTheme = utilsObject.merge(nextTheme, forcedThemeOverrides);

        setTheme(mergedTheme);

        return !!targetElement && applyTheme(mergedTheme, targetElement);
    };

    const value = {
        theme,
        setTheme: useCallback(setNextTheme, [setNextTheme]),
    };

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

const applyTheme = (applyableTheme: ITheme, targetElement: HTMLElement): boolean => {
    if (targetElement) {
        return applyThemeToHtmlElement({
            targetElement,
            theme: applyableTheme,
        });
    }

    return false;
};
